import React from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@material-ui/core";
import FingerprintIcon from "@material-ui/icons/Fingerprint";

import {
  AppToggleButtonGroup,
  AppDatePicker,
  AppToggleButton,
  AppSlider,
} from "./InputComponents";

import {
  races,
  genderIdentities,
  sexualPreference,
  relationshipStatuses,
} from "../../../data/db";

import useStyles from "./LetsGetKnow.style";

window.moment = moment;

const LetsGetKnow = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const isRaceChecked = (race) => {
    return props.selectedRaces.includes(race);
  };

  const isSexualPreferenceChecked = (preference) => {
    return props.selectedSexualPreferences.includes(preference);
  };

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box>
        <FingerprintIcon className={classes.textIcon} />
      </Box>
      <Typography variant="h4" className={classes.title}>
        Let's get to know each other
      </Typography>
      <Typography className={classes.subTitle}>
        I’m RepLI (pronounced Ree-ply) your reproductive mapping tool! I’m here
        to help you achieve your reproductive life goals.
      </Typography>
      {props.error && !props.validator.allValid() && (
        <Typography color="error">Please review your answers</Typography>
      )}
      {/* Date of Birth */}
      <Box className={classes.inputSection}>
        <Typography variant="h5">
          Date of Birth
          {
            <span
              className={
                props.error && !props.validator.fieldValid("dateOfBirth")
                  ? `${classes.asteriskError}`
                  : `${classes.asteriskNormal}`
              }
            >
              *
            </span>
          }
        </Typography>
        <AppDatePicker
          type="date"
          value={props.dateOfBirth}
          onChange={props.onDateBirthChange}
          error={props.error && !props.validator.fieldValid("dateOfBirth")}
          helperText={props.validator.message(
            "dateOfBirth",
            props.dateOfBirth,
            "required"
          )}
          inputProps={{
            max: new Date().toISOString().split("T")[0]
        }}
        />
      </Box>
      {/* Ethnicity */}
      <Box className={classes.inputSection}>
        <Typography variant="h5">
          Race/Ethnicity
          {
            <span
              className={
                props.error && !props.validator.fieldValid("races")
                  ? `${classes.asteriskError}`
                  : `${classes.asteriskNormal}`
              }
            >
              *
            </span>
          }
        </Typography>
        <FormControl
          error={props.error && !props.validator.fieldValid("races")}
        >
          <AppToggleButtonGroup>
            {races.map((race) => (
              <AppToggleButton
                key={race}
                selected={isRaceChecked(race)}
                onChange={() => props.onSelectedRacesChange(race)}
                name={race}
                label={race}
              />
            ))}
          </AppToggleButtonGroup>
          <FormHelperText>
            {props.validator.message("races", props.selectedRaces, "required")}
          </FormHelperText>
        </FormControl>
        <Box
          display={isRaceChecked("Prefer to self-describe") ? "block" : "none"}
        >
          <TextField
            variant="outlined"
            label="Self-describe"
            margin="normal"
            fullWidth
            inputProps={{ maxLength: 2500 }}
            value={props.raceSelfDescribe}
            onChange={(e) =>
              props.onRaceSelfDescribeChange(e, "Race Self Describe")
            }
            error={
              props.error && !props.validator.fieldValid("raceSelfDescribe")
            }
            helperText={props.validator.message(
              "raceSelfDescribe",
              props.raceSelfDescribe,
              `race_self_describe:${props.selectedRaces}`
            )}
          />
        </Box>
      </Box>
      {/* Gender */}
      <Box className={classes.inputSection}>
        <Typography variant="h5">
          Sex Assigned at Birth
          {
            <span
              className={
                props.error && !props.validator.fieldValid("gender")
                  ? `${classes.asteriskError}`
                  : `${classes.asteriskNormal}`
              }
            >
              *
            </span>
          }
        </Typography>
        <FormControl
          component="fieldset"
          error={props.error && !props.validator.fieldValid("gender")}
        >
          <RadioGroup
            row
            aria-label="Gender"
            value={props.gender}
            onChange={props.onGenderChange}
          >
            <FormControlLabel
              value="female"
              control={<Radio color="primary" />}
              label={t("common.substantive.female")}
            />
            <FormControlLabel
              value="male"
              control={<Radio color="primary" />}
              label={t("common.substantive.male")}
            />
            <FormControlLabel
              value="intersex"
              control={<Radio color="primary" />}
              label={t("common.substantive.intersex")}
            />
            <FormControlLabel
              value="preferNotToAnswer"
              control={<Radio color="primary" />}
              label={t("common.substantive.PNA")}
            />
          </RadioGroup>
          <FormHelperText>
            {props.validator.message("gender", props.gender, "required")}
          </FormHelperText>
        </FormControl>
      </Box>
      {/* Gender Identity */}
      <Box className={classes.inputSection}>
        <Typography variant="h5">
          Gender Identity
          {
            <span
              className={
                props.error && !props.validator.fieldValid("genderIdentity")
                  ? `${classes.asteriskError}`
                  : `${classes.asteriskNormal}`
              }
            >
              *
            </span>
          }
        </Typography>
        <FormControl
          component="fieldset"
          error={props.error && !props.validator.fieldValid("genderIdentity")}
        >
          <RadioGroup
            row
            aria-label="Gender Identity"
            value={props.genderIdentity}
            onChange={props.onGenderIdentityChange}
          >
            {genderIdentities.map((identity, i) => (
              <FormControlLabel
                key={i}
                value={identity}
                control={<Radio color="primary" />}
                label={identity}
              />
            ))}
          </RadioGroup>
          <FormHelperText>
            {props.validator.message(
              "genderIdentity",
              props.genderIdentity,
              "required"
            )}
          </FormHelperText>
        </FormControl>
        <Box
          display={
            props.genderIdentity === "Gender identity not listed"
              ? "block"
              : "none"
          }
        >
          <TextField
            variant="outlined"
            label="Please describe your gender identity"
            margin="normal"
            fullWidth
            inputProps={{ maxLength: 2500 }}
            value={props.genderIdentitySelfDescribe}
            onChange={(e) =>
              props.onGenderIdentitySelfDescribeChange(
                e,
                "Gender Identity Self Describe"
              )
            }
            error={
              props.error &&
              !props.validator.fieldValid("genderIdentitySelfDescribe")
            }
            helperText={props.validator.message(
              "genderIdentitySelfDescribe",
              props.genderIdentitySelfDescribe,
              `gender_identity_self_describe:${props.genderIdentity}`
            )}
          />
        </Box>
      </Box>
      {/* Sexual Orientation */}
      <Box className={classes.inputSection}>
        <Typography variant="h5">
          Sexual Orientation
          {
            <span
              className={
                props.error && !props.validator.fieldValid("sexualPreferences")
                  ? `${classes.asteriskError}`
                  : `${classes.asteriskNormal}`
              }
            >
              *
            </span>
          }
        </Typography>
        <FormControl
          error={
            props.error && !props.validator.fieldValid("sexualPreferences")
          }
        >
          <AppToggleButtonGroup>
            {sexualPreference.map((preference) => (
              <AppToggleButton
                key={preference}
                selected={isSexualPreferenceChecked(preference)}
                onChange={() => props.onSexualPreferenceChange(preference)}
                name={preference}
                label={preference}
              />
            ))}
          </AppToggleButtonGroup>
          <FormHelperText>
            {props.validator.message(
              "sexualPreferences",
              props.selectedSexualPreferences,
              "required"
            )}
          </FormHelperText>
        </FormControl>
        <Box
          display={
            isSexualPreferenceChecked("Prefer to self-describe")
              ? "block"
              : "none"
          }
        >
          <TextField
            variant="outlined"
            label="Self-describe"
            margin="normal"
            fullWidth
            inputProps={{ maxLength: 2500 }}
            value={props.sexualPreferenceSelfDescribe}
            onChange={(e) =>
              props.onSexualPreferenceSelfDescribeChange(
                e,
                "Sexual Preference Self Describe"
              )
            }
            error={
              props.error &&
              !props.validator.fieldValid("sexualPreferenceSelfDescribe")
            }
            helperText={props.validator.message(
              "sexualPreferenceSelfDescribe",
              props.sexualPreferenceSelfDescribe,
              `sexual_preference_self_describe:${props.selectedSexualPreferences}`
            )}
          />
        </Box>
      </Box>
      {/* Relationship Status */}
      <Box className={classes.inputSection}>
        <Typography variant="h5">
          Relationship Status
          {
            <span
              className={
                props.error && !props.validator.fieldValid("relationshipStatus")
                  ? `${classes.asteriskError}`
                  : `${classes.asteriskNormal}`
              }
            >
              *
            </span>
          }
        </Typography>
        <FormControl
          component="fieldset"
          error={
            props.error && !props.validator.fieldValid("relationshipStatus")
          }
        >
          <RadioGroup
            row
            aria-label="Relationship Status"
            value={props.relationshipStatus}
            onChange={props.onRelationshipStatusChange}
          >
            {relationshipStatuses.map((status, i) => (
              <FormControlLabel
                key={i}
                value={status}
                control={<Radio color="primary" />}
                label={status}
              />
            ))}
          </RadioGroup>
          <FormHelperText>
            {props.validator.message(
              "relationshipStatus",
              props.relationshipStatus,
              "required"
            )}
          </FormHelperText>
        </FormControl>
      </Box>
      {/* Children Plan */}
      <Box className={classes.inputSection}>
        <Typography variant="h5">
          Are or were kids part of your reproductive life plan?
          {
            <span
              className={
                props.error && !props.validator.fieldValid("childrenPlan")
                  ? `${classes.asteriskError}`
                  : `${classes.asteriskNormal}`
              }
            >
              *
            </span>
          }
        </Typography>
        <FormControl
          component="fieldset"
          error={props.error && !props.validator.fieldValid("childrenPlan")}
        >
          <RadioGroup
            row
            aria-label="Children Plan"
            value={props.childrenPlan}
            onChange={props.onChildrenPlanChange}
          >
            {["Yes", "No", "Unsure"].map((answer, i) => (
              <FormControlLabel
                key={i}
                value={answer}
                control={<Radio color="primary" />}
                label={answer}
              />
            ))}
          </RadioGroup>
          <FormHelperText>
            {props.validator.message(
              "childrenPlan",
              props.childrenPlan,
              "required"
            )}
          </FormHelperText>
        </FormControl>
      </Box>

      {
        /*Planned child bearing years*/

        props.childrenPlan && props.childrenPlan !== "No" && (
          <Box className={classes.inputSection}>
            <Typography variant="h5">
              Do you have a desire childbearing start age and end age?
              {
                <span
                  className={
                    props.error && !props.validator.fieldValid("plannedChildBearingYears")
                      ? `${classes.asteriskError}`
                      : `${classes.asteriskNormal}`
                  }
                >
                  *
                </span>
              }
            </Typography>
            <FormControl
              component="fieldset"
              error={props.error && !props.validator.fieldValid("plannedChildBearingYears")}
            >
              <RadioGroup
                row
                aria-label="Planned Child Bearing Years"
                value={props.plannedChildBearingYears}
                onChange={props.handlePlannedChildBearingYears}
              >
                {["Yes", "No"].map((answer, i) => (
                  <FormControlLabel
                    key={i}
                    value={answer}
                    control={<Radio color="primary" />}
                    label={answer}
                  />
                ))}
              </RadioGroup>
              <FormHelperText>
                {props.validator.message(
                  "plannedChildBearingYears",
                  props.plannedChildBearingYears,
                  "required"
                )}
              </FormHelperText>
            </FormControl>
          </Box>
        )
      }
      {
        /* Chilbearing Years */
        props.plannedChildBearingYears && props.plannedChildBearingYears !== "No" && (
          <Box className={classes.inputSection}>
            <Typography variant="h5">
              What are your desired childbearing years?
            </Typography>
            <AppSlider
              valueLabelDisplay="on"
              min={14}
              max={50}
              value={props.childbearingYears}
              onChange={props.onChildbearingYearsChange}
              marks={[
                {
                  value: 14,
                  label: "14",
                },
                {
                  value: 50,
                  label: "50",
                },
              ]}
            />
          </Box>
        )
      }
    </Box>
  );
};

export default LetsGetKnow;
